import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { InsuranceEnrollmentService } from '../../services/insuranceEnrollment.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { IEnrollAllResult, IInsuranceEnrollment, IInsurancePolicy } from '../../interfaces/insurance.interface';
import { displayUsername } from '../../utils/userUtils';
import { formatDate } from '../../utils/dateUtils';
import { EmployeeAutocomplete, EmployeeOption } from '../common/EmployeeAutocomplete';

interface Props {
    institutionId: string;
    policy: IInsurancePolicy;
    onEnrollmentChange?: () => void;
}

/**
 * Enrollment management for the active policy, spec D2/D7 + REV 2 R8/R9. Uses the SHARED
 * {@code EmployeeAutocomplete} (house rule: never hand-roll a picker) and disables/marks an
 * option already holding an open enrollment on this policy (L4: the picker has no built-in notion
 * of enrollment state).
 */
const enrollmentName = (e: IInsuranceEnrollment): string =>
    e.fullName || (e.username ? displayUsername(e.username) : '') || 'Unknown employee';

const EnrollmentManagement: React.FC<Props> = ({ institutionId, policy, onEnrollmentChange }) => {
    const canManage = usePermission(PERMISSIONS.INSURANCE_MANAGE);
    const toast = useToast();
    const [enrollments, setEnrollments] = useState<IInsuranceEnrollment[]>([]);
    const [selected, setSelected] = useState<EmployeeOption | null>(null);
    const [coverageOverride, setCoverageOverride] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [enrollAllPreview, setEnrollAllPreview] = useState<IEnrollAllResult | null>(null);
    const [confirmEnrollAllOpen, setConfirmEnrollAllOpen] = useState(false);
    const [enrollingAll, setEnrollingAll] = useState(false);
    const [endTarget, setEndTarget] = useState<IInsuranceEnrollment | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const [list, preview] = await Promise.all([
                InsuranceEnrollmentService.listOpenForPolicy(institutionId, policy.id),
                InsuranceEnrollmentService.previewEnrollAll(institutionId, policy.id),
            ]);
            setEnrollments([...list].sort((a, b) => enrollmentName(a).localeCompare(enrollmentName(b), undefined, { sensitivity: 'base' })));
            setEnrollAllPreview(preview);
        } catch (e) {
            toast.showApiError(e, 'Failed to load enrollments');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policy.id]);

    const enrolledUserIds = new Set(enrollments.map(e => e.userId));

    const handleEnroll = async () => {
        if (!selected) return;
        try {
            await InsuranceEnrollmentService.create(institutionId, {
                policyId: policy.id,
                userId: selected.id,
                coverageOverride: coverageOverride === '' ? null : Number(coverageOverride),
            });
            toast.showSuccess('Enrolled');
            setSelected(null);
            setCoverageOverride('');
            await load();
            onEnrollmentChange?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to enroll');
        }
    };

    const handleEnd = async (id: string) => {
        try {
            await InsuranceEnrollmentService.end(institutionId, id);
            toast.showSuccess('Enrollment ended');
            setEndTarget(null);
            await load();
            onEnrollmentChange?.();
        } catch (e) {
            toast.showApiError(e, 'Failed to end enrollment');
        }
    };

    const handleConfirmEnrollAll = async () => {
        setEnrollingAll(true);
        try {
            const result = await InsuranceEnrollmentService.enrollAll(institutionId, policy.id);
            toast.showSuccess(`Enrolled ${result.createdCount} employee(s)`);
            setConfirmEnrollAllOpen(false);
            await load();
            onEnrollmentChange?.();
        } catch (e) {
            toast.showApiError(e, 'Enroll all failed');
        } finally {
            setEnrollingAll(false);
        }
    };

    return (
        <Paper sx={{ p: 2, mb: 3 }} data-testid="insurance-enrollment-management" data-help-section="insurance.enroll.section">
            <Typography variant="h6" mb={2}>Enrollments</Typography>

            {canManage && (
            <Box display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap" data-help-section="insurance.enroll.toolbar">
                <EmployeeAutocomplete
                    value={selected}
                    onChange={setSelected}
                    label="Enroll employee" data-help="insurance.enroll.employee"
                    activeOnly
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.fullName || option.username}
                            {enrolledUserIds.has(option.id) ? ' (already enrolled)' : ''}
                        </li>
                    )}
                    sx={{ flex: '1 1 260px', maxWidth: 400 }}
                />
                <TextField data-help="insurance.enroll.override" size="small" label="Coverage Override (optional)" type="number" value={coverageOverride}
                    onChange={e => setCoverageOverride(e.target.value)} sx={{ flex: '1 1 180px', maxWidth: 260 }} />
                <Button data-help="insurance.enroll.enroll" variant="outlined" onClick={handleEnroll} disabled={!selected || enrolledUserIds.has(selected.id)}>
                    Enroll
                </Button>
                <Button
                    data-help="insurance.enroll.enrollAll"
                    variant="contained"
                    onClick={() => setConfirmEnrollAllOpen(true)}
                    disabled={!enrollAllPreview || enrollAllPreview.createdCount === 0}
                    data-testid="enroll-all-button"
                >
                    {enrollAllPreview && enrollAllPreview.createdCount > 0
                        ? `Enroll ${enrollAllPreview.createdCount} remaining ${enrollAllPreview.createdCount === 1 ? 'member' : 'members'}`
                        : 'Enroll all active members'}
                </Button>
            </Box>
            )}

            {!loading && enrollAllPreview && enrollAllPreview.createdCount === 0 && enrollAllPreview.eligibleCount > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>Every active member is already enrolled on this policy.</Alert>
            )}

            <TableContainer data-help-section="insurance.enroll.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="insurance.enroll.col.employee">Employee</TableCell>
                            <TableCell data-help="insurance.enroll.col.enrolledOn">Enrolled On</TableCell>
                            <TableCell align="right" data-help="insurance.enroll.col.coverage">Coverage</TableCell>
                            <TableCell align="right" data-help="insurance.enroll.col.action">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {enrollments.length === 0 && (
                            <TableRow><TableCell colSpan={4}><Typography color="text.secondary">No open enrollments.</Typography></TableCell></TableRow>
                        )}
                        {enrollments.map(e => (
                            <TableRow key={e.id} hover>
                                <TableCell>{enrollmentName(e)}</TableCell>
                                <TableCell>{formatDate(e.enrolledOn)}</TableCell>
                                <TableCell align="right">{e.effectiveCoverage.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    {canManage && <Button data-help="insurance.enroll.action.end" size="small" color="warning" onClick={() => setEndTarget(e)}>End</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!endTarget} onClose={() => setEndTarget(null)} PaperProps={{ 'data-help-page': 'insurance.endEnroll.page' } as any}>
                <DialogTitle>End this enrollment?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {endTarget ? enrollmentName(endTarget) : ''} will no longer be covered by this policy.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEndTarget(null)} data-help="insurance.endEnroll.cancel">Cancel</Button>
                    <Button color="warning" variant="contained" data-help="insurance.endEnroll.confirm" onClick={() => endTarget && handleEnd(endTarget.id)}>
                        End enrollment
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmEnrollAllOpen} onClose={() => setConfirmEnrollAllOpen(false)} PaperProps={{ 'data-help-page': 'insurance.enrollAllConfirm.page' } as any}>
                <DialogTitle>Enroll all active members?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will enroll {enrollAllPreview?.createdCount ?? 0} active member(s) who are not
                        already enrolled on this policy. This count is re-verified on the server, so it may
                        differ slightly if membership changed since this dialog opened.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmEnrollAllOpen(false)} data-help="insurance.enrollAllConfirm.cancel">Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmEnrollAll} data-help="insurance.enrollAllConfirm.confirm" disabled={enrollingAll}>Enroll All</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default EnrollmentManagement;
